import { useState } from "react";
import { Phone } from "lucide-react";
import PhoneInput from "react-phone-number-input";
import { isValidPhoneNumber } from "libphonenumber-js";
import { useLanguage } from "../../../context/LanguageContext";

type Props = {
  value: string;
  onChange: (fullPhone: string) => void;
  onBlur?: () => void;
  error?: string;
  required?: boolean;
  phoneExists?: boolean;
  checkingPhone?: boolean;
  label?: string;
  name?: string;
  /** Fires whenever the selected/detected country changes — capture this to
   *  send alongside the phone value so the backend can cross-check the
   *  number actually belongs to the country the user selected. */
  onCountryChange?: (country?: string) => void;
};

const PhoneField = ({
  value = "",
  onChange,
  onBlur,
  error,
  required,
  phoneExists,
  checkingPhone,
  label,
  name,
  onCountryChange,
}: Props) => {
  const [touched, setTouched] = useState(false);
  const [focused, setFocused] = useState(false);
  const { t } = useLanguage();
  const resolvedLabel = label ?? t("shared.phoneField.defaultLabel");

  const showInvalid = touched && !focused && !!value && !isValidPhoneNumber(value);
  const borderCls = error || phoneExists || showInvalid ? "border-red-400" : "border-gray-200 hover:border-gray-300";

  return (
    <div className="w-full">
      <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
        <Phone size={13} className="text-[#b3d130]" />
        {resolvedLabel}
        {required && <span className="text-red-500">*</span>}
      </label>

      <PhoneInput
        international
        defaultCountry="EG"
        countryCallingCodeEditable={false}
        value={value || undefined}
        onChange={(v) => onChange(v ?? "")}
        onCountryChange={onCountryChange}
        name={name}
        required={required}
        containerComponentProps={{ dir: "ltr" }}
        numberInputProps={{
          dir: "ltr",
          onFocus: () => setFocused(true),
          onBlur: () => {
            setFocused(false);
            setTouched(true);
            onBlur?.();
          },
        }}
        className={`${borderCls} ${focused ? "PhoneInput--focus" : ""}`}
      />

      <div className="mt-1 min-h-[18px] text-xs">
        {error ? (
          <p className="text-red-500 flex items-center gap-1"><span>⚠</span>{error}</p>
        ) : phoneExists ? (
          <p className="text-red-500 flex items-center gap-1"><span>⚠</span>{t("shared.phoneField.alreadyRegistered")}</p>
        ) : checkingPhone ? (
          <p className="text-gray-400">{t("shared.phoneField.checking")}</p>
        ) : showInvalid ? (
          <p className="text-red-500 flex items-center gap-1"><span>⚠</span>{t("shared.phoneField.invalid")}</p>
        ) : null}
      </div>
    </div>
  );
};

export default PhoneField;
